import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import axios from 'axios';
import './Contact.css';

const API = process.env.REACT_APP_API_URL || 'https://api.blood.msdiscoveries.com/api';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    reveals.forEach(r => observer.observe(r));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Message sent! We\'ll get back to you soon.');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact BloodBridge — Reach Out to Save a Life</title>
        <meta name="description" content="Contact BloodBridge to register as a blood donor, request blood for a patient, or for any queries. We respond 24/7 for emergencies." />
        <link rel="canonical" href="https://blood.msdiscoveries.com/contact" />
      </Helmet>

      <div className="page-header">
        <div className="container">
          <div className="section-label reveal" style={{ justifyContent: 'center' }}>Get In Touch</div>
          <h1 className="reveal">We're Here to Help.<br /><em>Always.</em></h1>
          <p className="reveal">
            Need blood urgently? Want to register as a donor? Have a question? 
            Reach out — we respond to all emergency requests within minutes.
          </p>
        </div>
      </div>

      <section className="contact-section section">
        <div className="container">
          <div className="contact-grid">
            {/* Info */}
            <div className="contact-info reveal">
              <h2>How Can We Help?</h2>
              <p>
                Whether it's an emergency blood request, a donor registration, 
                or a general inquiry — our team is available around the clock.
              </p>

              <div className="contact-cards">
                <div className="contact-card">
                  <div className="contact-card-icon">📞</div>
                  <div>
                    <h4>Emergency Hotline</h4>
                    <a href="tel:+919876543210">+91 98765 43210</a>
                    <span>Available 24 hours, 7 days</span>
                  </div>
                </div>
                <div className="contact-card">
                  <div className="contact-card-icon">✉️</div>
                  <div>
                    <h4>Email Us</h4>
                    <a href="mailto:help@blood.msdiscoveries.com">help@blood.msdiscoveries.com</a>
                    <span>Response within 2 hours</span>
                  </div>
                </div>
                <div className="contact-card">
                  <div className="contact-card-icon">📍</div>
                  <div>
                    <h4>Main Office</h4>
                    <span>12, Anna Salai, Salem</span>
                    <span>Tamil Nadu 636001, India</span>
                  </div>
                </div>
                <div className="contact-card">
                  <div className="contact-card-icon">⏰</div>
                  <div>
                    <h4>Office Hours</h4>
                    <span>Mon–Sat: 8 AM – 8 PM</span>
                    <span>Emergency: 24/7</span>
                  </div>
                </div>
              </div>

              <div className="contact-faq">
                <h3>Common Requests</h3>
                {[
                  'Register as a new blood donor',
                  'Find a donor for urgent need',
                  'Verify a donor\'s details',
                  'Partner with us (hospital/blood bank)',
                  'Volunteer or support our mission'
                ].map((q, i) => (
                  <div key={i} className="faq-item">
                    <span className="faq-arrow">→</span>
                    <span>{q}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="contact-form-wrap reveal reveal-delay-2">
              <div className="contact-form-header">
                <h3>Send a Message</h3>
                <p>We'll respond within 2 hours for general queries.</p>
              </div>
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="name">Full Name *</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      className="form-input"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="email">Email *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className="form-input"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="phone">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    className="form-input"
                    placeholder="+91 XXXXX XXXXX"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-input contact-textarea"
                    placeholder="Describe your request — blood group needed, urgency, or any questions..."
                    value={form.message}
                    onChange={handleChange}
                    rows={6}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary contact-submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="submit-spinner" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <span>→</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="emergency-section">
        <div className="container">
          <div className="emergency-banner reveal">
            <div className="emergency-pulse">
              <div className="ep-dot" />
              <div className="ep-ring" />
            </div>
            <div className="emergency-text">
              <h3>Need Blood Right Now?</h3>
              <p>Call our emergency line — we'll connect you with an available donor in your area within 30 minutes.</p>
            </div>
            <a href="tel:+919876543210" className="btn btn-primary emergency-btn">
              📞 Call Emergency Line
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
